import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ScribeSocketClient } from '../../services/scribeSocketClient';
import { ScribeAudioEngine } from '../../services/scribeAudioEngine';
import LiveTranscript from './LiveTranscript';

const scribeSocket = new ScribeSocketClient();
const audioEngine = new ScribeAudioEngine();

// Must match SCRIBE_CONSENT_TEXT in src/services/scribe/ScribeSocketHandler.js — the server stores its copy on the session.
const SCRIBE_CONSENT_TEXT = 'I have informed the patient that this visit is being recorded and transcribed by an AI scribe to create clinical notes, and the patient has agreed.';

function ScribePanel({ practiceId, patient, appointmentId, specialties, noteTemplates, onSessionComplete, onBack }) {
  const [status, setStatus] = useState('idle'); // idle, connecting, ready, recording, paused, processing, completed, error
  const statusRef = useRef('idle'); // mirror of status for use in closures
  const [specialty, setSpecialty] = useState('PRIMARY_CARE');
  const [noteTemplate, setNoteTemplate] = useState('HISTORY_AND_PHYSICAL');
  // HIPAA: clinician attests patient consent before a stream can open. The
  // server refuses initScribe without it and records who/when on the session.
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [transcriptEntries, setTranscriptEntries] = useState([]);
  const [sessionGuid, setSessionGuid] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const pausedDurationRef = useRef(0);

  // Keep statusRef in sync with status state
  useEffect(() => { statusRef.current = status; }, [status]);

  // Timer management
  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000) + pausedDurationRef.current;
      setDuration(elapsed);
    }, 1000);
  }, []);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      pausedDurationRef.current = duration;
    }
  }, [duration]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Socket event handlers
  useEffect(() => {
    const token = localStorage.getItem('nh_auth_token');
    if (!token) {
      console.error('[ScribePanel] No auth token found in localStorage — socket will not connect');
      return;
    }
    console.log('[ScribePanel] Connecting to /scribe namespace...');

    scribeSocket
      .on('connect', () => {
        console.log('[ScribePanel] Socket connected');
      })
      .on('scribeReady', (data) => {
        console.log('[ScribePanel] scribeReady received:', data);
        setSessionGuid(data.sessionGuid);
        setStatus('ready');
        setError(null);
        // Auto-start audio
        handleStartAudio().catch(err => {
          console.error('[ScribePanel] handleStartAudio failed:', err);
        });
      })
      .on('scribeTranscript', (data) => {
        setTranscriptEntries(prev => {
          // If partial, replace last entry with same role
          if (data.isPartial) {
            const updated = [...prev];
            const lastIdx = updated.length - 1;
            if (lastIdx >= 0 && updated[lastIdx].isPartial && updated[lastIdx].role === data.role) {
              updated[lastIdx] = data;
              return updated;
            }
          }
          return [...prev, data];
        });
      })
      .on('scribeStatus', (data) => {
        console.log('[ScribePanel] Status:', data.status, data.message);
        if (data.status === 'processing') setStatus('processing');
      })
      .on('scribeNotesReady', (data) => {
        console.log('[ScribePanel] scribeNotesReady:', data);
        setNotes(true); // notes are fetched via REST API on the review page
        setStatus('completed');
        stopTimer();
      })
      .on('scribeError', (data) => {
        console.error('[ScribePanel] Error:', data.message);
        setError(data.message);
        if (['connecting', 'ready'].includes(statusRef.current)) setStatus('error');
      })
      .on('scribeSessionClosed', (data) => {
        console.log('[ScribePanel] Session closed:', data.reason);
        stopTimer();
        if (statusRef.current !== 'completed') setStatus('idle');
      })
      .on('error', (msg) => {
        console.error('[ScribePanel] Socket connection error:', msg);
        setError('Connection failed: ' + msg);
        if (['connecting', 'idle'].includes(statusRef.current)) setStatus('error');
      })
      .on('disconnect', () => {
        console.log('[ScribePanel] Socket disconnected');
      })
      .connect({ token });

    return () => {
      audioEngine.stopCapture();
      scribeSocket.disconnect();
      stopTimer();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartSession = async () => {
    setStatus('connecting');
    setError(null);
    setTranscriptEntries([]);
    setNotes(null);
    setDuration(0);
    pausedDurationRef.current = 0;

    // Warmup AudioContext during user gesture (button click) to avoid
    // Chrome autoplay policy blocking it in the socket callback later
    await audioEngine.warmup();

    if (!scribeSocket.isConnected) {
      setError('Socket not connected. Please refresh the page and try again.');
      setStatus('error');
      return;
    }

    scribeSocket.initScribe({
      appointmentId: appointmentId || null,
      patientId: patient.id,
      practiceId,
      specialty,
      noteTemplate,
      consentConfirmed
    });

    // Timeout: if scribeReady not received within 15s, show error
    setTimeout(() => {
      if (statusRef.current === 'connecting') {
        setError('Session startup timed out. Check that AWS HealthScribe is configured for this environment.');
        setStatus('error');
      }
    }, 15000);
  };

  const handleStartAudio = async () => {
    console.log('[ScribePanel] handleStartAudio called');
    try {
      scribeSocket.startAudio();
      console.log('[ScribePanel] startAudio emitted, starting mic capture...');
      let chunkCount = 0;
      await audioEngine.startCapture((base64Audio) => {
        chunkCount++;
        if (chunkCount <= 3 || chunkCount % 50 === 0) {
          console.log(`[ScribePanel] Audio chunk #${chunkCount}, size: ${base64Audio.length}`);
        }
        scribeSocket.sendAudio(base64Audio);
      }, (source) => scribeSocket.reportAudioSource(source));
      console.log('[ScribePanel] Mic capture started successfully');
      setStatus('recording');
      startTimer();
    } catch (err) {
      console.error('[ScribePanel] Audio capture error:', err);
      setError('Microphone access denied. Please allow microphone access and try again.');
      setStatus('error');
    }
  };

  const handlePause = () => {
    audioEngine.stopCapture();
    scribeSocket.pause();
    pauseTimer();
    setStatus('paused');
  };

  const handleResume = async () => {
    try {
      scribeSocket.resume();
      await audioEngine.startCapture((base64Audio) => {
        scribeSocket.sendAudio(base64Audio);
      });
      setStatus('recording');
      startTimer();
    } catch (err) {
      setError('Failed to resume microphone capture.');
    }
  };

  const handleEnd = () => {
    if (!window.confirm('End this scribe session? Notes will be generated from the recording.')) return;
    audioEngine.stopCapture();
    scribeSocket.endSession();
    stopTimer();
    setStatus('processing');
  };

  const handleCancel = () => {
    if (!window.confirm('Cancel this session? No notes will be generated.')) return;
    audioEngine.stopCapture();
    scribeSocket.disconnect();
    stopTimer();
    setStatus('idle');
    if (onBack) onBack();
  };

  const formatDuration = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const isSessionActive = ['connecting', 'ready', 'recording', 'paused', 'processing'].includes(status);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          {!isSessionActive && (
            <button style={styles.backBtn} onClick={onBack}>
              <BackIcon /> Back
            </button>
          )}
          <div>
            <h2 style={styles.title}>Ambient Scribe</h2>
            {patient && (
              <span style={styles.patientInfo}>
                {patient.first_name} {patient.last_name}
                {appointmentId && ` | Appt #${appointmentId}`}
              </span>
            )}
          </div>
        </div>
        <div style={styles.headerRight}>
          <StatusIndicator status={status} />
          {isSessionActive && (
            <span style={styles.timer}>{formatDuration(duration)}</span>
          )}
        </div>
      </div>

      {/* Config (only shown before session starts) */}
      {status === 'idle' && (
        <div style={styles.configSection}>
          <div style={styles.configRow}>
            <div style={styles.configField}>
              <label style={styles.configLabel}>Specialty</label>
              <select
                style={styles.configSelect}
                value={specialty}
                onChange={e => setSpecialty(e.target.value)}
              >
                {(specialties || []).map(s => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.configField}>
              <label style={styles.configLabel}>Note Template</label>
              <select
                style={styles.configSelect}
                value={noteTemplate}
                onChange={e => setNoteTemplate(e.target.value)}
              >
                {(noteTemplates || []).map(t => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label style={styles.consentRow}>
            <input type="checkbox" checked={consentConfirmed} onChange={(e) => setConsentConfirmed(e.target.checked)} style={{ marginTop: 3 }} />
            <span>{SCRIBE_CONSENT_TEXT}</span>
          </label>
          <button style={{ ...styles.startBtn, ...(consentConfirmed ? {} : styles.startBtnDisabled) }} onClick={handleStartSession} disabled={!consentConfirmed} title={consentConfirmed ? '' : 'Confirm patient consent first'}>
            <MicIcon /> Start Recording
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={styles.errorBanner}>
          {error}
          <button style={styles.errorDismiss} onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Controls */}
      {isSessionActive && (
        <div style={styles.controls}>
          {status === 'recording' && (
            <>
              <button style={styles.pauseBtn} onClick={handlePause}>
                <PauseIcon /> Pause
              </button>
              <button style={styles.endBtn} onClick={handleEnd}>
                <StopIcon /> End Session
              </button>
            </>
          )}
          {status === 'paused' && (
            <>
              <button style={styles.resumeBtn} onClick={handleResume}>
                <MicIcon /> Resume
              </button>
              <button style={styles.endBtn} onClick={handleEnd}>
                <StopIcon /> End Session
              </button>
            </>
          )}
          {status === 'connecting' && (
            <span style={styles.connectingText}>Connecting to HealthScribe...</span>
          )}
          {status === 'processing' && (
            <span style={styles.processingText}>
              <SpinnerIcon /> Generating clinical notes...
            </span>
          )}
          <button style={styles.cancelBtn} onClick={handleCancel}>
            Cancel
          </button>
        </div>
      )}

      {/* Live Transcript */}
      {isSessionActive && (
        <div style={styles.transcriptSection}>
          <h3 style={styles.sectionTitle}>Live Transcript</h3>
          <div style={styles.transcriptBox}>
            <LiveTranscript entries={transcriptEntries} />
          </div>
        </div>
      )}

      {/* Completed — notes preview */}
      {status === 'completed' && notes && (
        <div style={styles.completedSection}>
          <div style={styles.completedBanner}>
            <CheckIcon /> Session complete — notes generated successfully
          </div>
          <p style={styles.completedText}>
            Duration: {formatDuration(duration)} | Notes are ready for review.
          </p>
          <button style={styles.reviewBtn} onClick={() => onSessionComplete && onSessionComplete(sessionGuid)}>
            Review Notes
          </button>
        </div>
      )}
    </div>
  );
}

function StatusIndicator({ status }) {
  const map = {
    idle: { color: '#9ca3af', label: 'Ready' },
    connecting: { color: '#f59e0b', label: 'Connecting' },
    ready: { color: '#f59e0b', label: 'Ready' },
    recording: { color: '#ef4444', label: 'Recording' },
    paused: { color: '#f59e0b', label: 'Paused' },
    processing: { color: '#8b5cf6', label: 'Processing' },
    completed: { color: '#10b981', label: 'Complete' },
    error: { color: '#ef4444', label: 'Error' }
  };
  const s = map[status] || map.idle;
  return (
    <span style={styles.statusIndicator}>
      <span style={{
        ...styles.statusDot,
        backgroundColor: s.color,
        ...(status === 'recording' ? styles.statusDotPulse : {})
      }} />
      {s.label}
    </span>
  );
}

// Icons
function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="6" y="4" width="4" height="16" />
      <rect x="14" y="4" width="4" height="16" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="16" height="16" rx="2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
      <path d="M21 12a9 9 0 11-6.219-8.56" />
    </svg>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '100%'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: '#fff',
    borderRadius: '10px',
    border: '1px solid #e5e7eb'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#374151',
    fontSize: '13px',
    cursor: 'pointer'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827'
  },
  patientInfo: {
    fontSize: '13px',
    color: '#6b7280'
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151'
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block'
  },
  statusDotPulse: {
    boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.7)',
    animation: 'pulse 1.5s infinite'
  },
  timer: {
    fontSize: '20px',
    fontWeight: 700,
    fontFamily: 'monospace',
    color: '#111827'
  },
  configSection: {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  configRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap'
  },
  configField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: '200px'
  },
  configLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151'
  },
  configSelect: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    backgroundColor: '#fff',
    color: '#1f2937',
    textTransform: 'capitalize'
  },
  consentRow: { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', margin: '12px 0', cursor: 'pointer' },
  startBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  startBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#dc2626',
    color: '#fff',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-start'
  },
  errorBanner: {
    padding: '10px 16px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '8px',
    fontSize: '13px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  errorDismiss: {
    padding: '4px 10px',
    borderRadius: '4px',
    border: '1px solid #fca5a5',
    backgroundColor: '#fff',
    color: '#dc2626',
    fontSize: '12px',
    cursor: 'pointer'
  },
  controls: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    padding: '12px 20px',
    backgroundColor: '#fff',
    borderRadius: '10px',
    border: '1px solid #e5e7eb'
  },
  pauseBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 20px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#374151',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer'
  },
  resumeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer'
  },
  endBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#dc2626',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer'
  },
  cancelBtn: {
    marginLeft: 'auto',
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#6b7280',
    fontSize: '13px',
    cursor: 'pointer'
  },
  connectingText: {
    color: '#f59e0b',
    fontSize: '14px',
    fontWeight: 500
  },
  processingText: {
    color: '#8b5cf6',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  transcriptSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    minHeight: '300px'
  },
  sectionTitle: {
    margin: 0,
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    borderBottom: '1px solid #e5e7eb'
  },
  transcriptBox: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden'
  },
  completedSection: {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center'
  },
  completedBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#065f46',
    fontSize: '16px',
    fontWeight: 600
  },
  completedText: {
    margin: 0,
    color: '#6b7280',
    fontSize: '14px'
  },
  reviewBtn: {
    padding: '10px 28px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer'
  }
};

export default ScribePanel;
